import { useState } from 'react';

import { Modal, ModalType } from '@@/modals';
import { Button } from '@@/buttons';
import { RadioGroup, RadioGroupOption } from '@@/RadioGroup/RadioGroup';
import { Alert } from '@@/Alert';

export type WorkflowDeleteMode = 'detach' | 'destroy';

const DELETE_MODE_OPTIONS: ReadonlyArray<RadioGroupOption<WorkflowDeleteMode>> =
  [
    {
      value: 'detach',
      label: (
        <span>
          <span className="font-semibold">Detach</span> — remove the workflow
          but keep its deployed resources running, unlinked from GitOps.
        </span>
      ),
    },
    {
      value: 'destroy',
      label: (
        <span>
          <span className="font-semibold">Destroy</span> — remove the workflow
          and delete its deployed resources.
        </span>
      ),
    },
  ];

interface Props {
  onConfirm: (mode: WorkflowDeleteMode) => void;
  onCancel: () => void;
}

export function WorkflowDeleteModal({ onConfirm, onCancel }: Props) {
  const [mode, setMode] = useState<WorkflowDeleteMode>('detach');

  return (
    <Modal onDismiss={onCancel} aria-label="Delete workflow">
      <Modal.Header title="Delete workflow" modalType={ModalType.Destructive} />
      <Modal.Body>
        <div className="flex flex-col gap-3">
          <p className="!mb-0">Choose how to delete this workflow.</p>
          <RadioGroup
            name="workflow-delete-mode"
            selectedOption={mode}
            onOptionChange={setMode}
            groupClassName="flex flex-col gap-2"
            options={DELETE_MODE_OPTIONS}
          />
          {mode === 'destroy' && (
            <Alert color="warn">
              This will permanently delete the deployed resources for every
              artifact in this workflow. This action cannot be undone.
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={onCancel}
          color="default"
          data-cy="workflow-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(mode)}
          color={mode === 'destroy' ? 'danger' : 'dangerlight'}
          data-cy="workflow-delete-confirm"
        >
          {mode === 'destroy' ? 'Destroy' : 'Detach'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
